import { notification, Steps } from 'antd'
import clsx from 'clsx'
import { useMemo, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { parseEther } from 'viem'
import { useChainId, useWriteContract } from 'wagmi'

import BookDark from '@/assets/books-dark.png'
import BookLight from '@/assets/books-light.png'
import error_icon from '@/assets/error_icon.png'
import success_icon from '@/assets/success_icon.png'
import { config } from '@/config'
import { useLocalStorage } from '@/context/localstorage-context'
import type { PosStakeRegisterTransParamsInterface } from '@/types/trans-params'
import { wanPubKey2Address } from '@/utils'

import { Link } from '../pos-detail.module'

import StepFour from './step-four'
import StepOne from './step-one'
import StepThree from './step-three'
import StepTwo_1 from './step-two_1'
import StepTwo_2 from './step-two_2'

const initTransParam: PosStakeRegisterTransParamsInterface = {
  secPk: '',
  bn256Pk: '',
  lockEpochs: 0,
  feeRate: 0,
  maxFeeRate: 0,
  amount: '0',
  isAcceptDelegation: true,
}

export default function SetupPos() {
  const chainId = useChainId()
  const [step, setStep] = useState(0)
  const { theme } = useLocalStorage()

  const [tarnsParam, setTransParam] = useState(initTransParam)
  const isdark = useMemo(() => theme === 'dark', [theme])

  const { data: hash, writeContractAsync, isPending } = useWriteContract()
  const PosContractConfig = config[chainId]['PosContract']

  const posStakeRegister = async () => {
    try {
      const hash = await writeContractAsync({
        address: PosContractConfig.addr,
        abi: PosContractConfig.abi,
        functionName: 'stakeRegister',
        args: [
          tarnsParam.secPk,
          tarnsParam.bn256Pk,
          tarnsParam.lockEpochs,
          Math.round(
            tarnsParam.isAcceptDelegation ? tarnsParam.feeRate * 100 : 10000,
          ),
          Math.round(
            tarnsParam.isAcceptDelegation ? tarnsParam.maxFeeRate * 100 : 10000,
          ),
        ],
        value: parseEther(tarnsParam.amount),
      })
      notification.success({
        message: 'Transaction completed',
        description: <Link txHash={hash} />,
        className: `${isdark ? 'noticeSuccess_dark' : 'noticeSuccess_light'}`,
        icon: <img width={30} src={success_icon} alt="notification" />,
      })
      setStep(3)
    } catch (error) {
      console.error('posStakeRegister', error)
      notification.error({
        message: 'Transaction Sent Failed',
        description: <Link txHash={hash || ''} />,
        className: `${isdark ? 'noticeSuccess_dark' : 'noticeSuccess_light'}`,
        icon: <img width={30} src={error_icon} alt="notification" />,
      })
    }
  }

  const onChange = (currentStep: number) => {
    if (currentStep === 1) {
      setStep(11)
    } else {
      setStep(currentStep)
    }
  }

  return (
    <div className="flex justify-center p-[1rem] mt-[30px]">
      <div className="flex gap-x-[3rem] justify-center md:flex-col-reverse md:w-full">
        <div className="min-w-[40%] bg-white rounded-2xl h-fit max-w-[1000px] min-h-[300px] p-[3rem] dark:bg-dark-222 md:min-h-fit">
          {step === 0 && <StepOne setStep={setStep} />}
          {step === 11 && (
            <StepTwo_1
              setStep={setStep}
              setTransParam={setTransParam}
              tarnsParam={tarnsParam}
            />
          )}
          {step === 12 && (
            <StepTwo_2
              isPending={isPending}
              setStep={setStep}
              handleSendTrans={posStakeRegister}
              tarnsParam={tarnsParam}
            />
          )}
          {step === 2 && (
            <StepThree
              setStep={setStep}
              wkAddr={
                tarnsParam.secPk ? wanPubKey2Address(tarnsParam.secPk) : 'N/A'
              }
            />
          )}
          {step === 3 && <StepFour />}
        </div>
        <div className="flex flex-col md:mb-[20px] md:gap-4">
          <Steps
            className={clsx('min-h-[300px] md:min-h-fit', {
              'osm-steps-light': !isdark,
              'osm-steps-dark': isdark,
            })}
            direction={isMobile ? 'horizontal' : 'vertical'}
            size="small"
            responsive={false}
            onChange={onChange}
            current={[11, 12].includes(step) ? 1 : step}
            items={[
              { title: !isMobile && 'Overview' },
              {
                title: !isMobile && 'Register',
              },
              {
                title: !isMobile && 'Configure',
              },
              {
                title: !isMobile && 'Monitor',
              },
            ]}
          />
          <div
            className="bg-white rounded-xl px-4 py-2 cursor-pointer flex gap-2 dark:bg-dark-222 relative top-[-3rem] md:sticky"
            onClick={() =>
              window.open(
                'https://docs.wanchain.org/pos-validator-nodes/setup-a-pos-node-via-xstake-recommended',
                '_blank',
              )
            }
          >
            <img
              src={isdark ? BookDark : BookLight}
              alt="setup-pos"
              className="w-[20px]"
            />
            <span className="dark:text-white">Setup Guide</span>
          </div>
        </div>
      </div>
    </div>
  )
}
